import { useCallback, useEffect, useState } from "react";
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { Link, useLocation } from "react-router-dom";
import { endpoints } from "../api.js";
import { INVITE_ROLES } from "../config.js";
import { Icon } from "../components/Icon.jsx";
import { Banner, Credits, ErrorBanner, Loading, Spinner, StatusPill } from "../components/ui.jsx";
import { useDocumentTitle, useErrorMessage, useLocale, useLocalePath } from "../i18n/hooks.js";
import { canManage, useSession } from "../session.jsx";

function Section({ id, icon, titleId, children, action }) {
  return (
    <section className="card settings-section" id={id}>
      <div className="card-head">
        <h2 className="h3">
          <Icon name={icon} size={17} />
          <FormattedMessage id={titleId} />
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function regionName(code, locale) {
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(code) || code;
  } catch {
    return code;
  }
}

function Organisation() {
  const errMsg = useErrorMessage();
  const locale = useLocale();
  const { tenant, setTenant, role } = useSession();
  const max = tenant?.entitlements?.retentionDaysMax;
  const [days, setDays] = useState(tenant?.retentionDays ?? 30);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const editable = canManage(role);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const { tenant: t } = await endpoints.patchTenant({ retentionDays: Number(days) });
      setTenant(t);
      setSaved(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (!tenant) return <Loading />;
  return (
    <Section id="organisation" icon="shield" titleId="settings.org.title">
      <dl className="kv kv-cols">
        <div className="kv-row">
          <dt>
            <FormattedMessage id="onboarding.org.name" />
          </dt>
          <dd>{tenant.name}</dd>
        </div>
        <div className="kv-row">
          <dt>
            <FormattedMessage id="onboarding.org.country" />
          </dt>
          <dd>{regionName(tenant.country, locale)}</dd>
        </div>
        <div className="kv-row">
          <dt>
            <FormattedMessage id="onboarding.org.vat" />
          </dt>
          <dd className="mono">{tenant.vatId || "—"}</dd>
        </div>
        <div className="kv-row">
          <dt>
            <FormattedMessage id="settings.org.status" />
          </dt>
          <dd>
            <StatusPill ns="tenantStatus" value={tenant.status} />
          </dd>
        </div>
        <div className="kv-row">
          <dt>
            <FormattedMessage id="dashboard.verification" />
          </dt>
          <dd>
            <span className="level-badge is-on">L{tenant.verificationLevel}</span>
          </dd>
        </div>
      </dl>
      <form className="form form-inline" onSubmit={save}>
        <label className="field">
          <span className="field-label">
            <FormattedMessage id="settings.org.retention" />
          </span>
          <div className="input-suffix">
            <input
              type="number"
              min={1}
              max={max || undefined}
              value={days}
              disabled={!editable}
              onChange={(e) => {
                setDays(e.target.value);
                setSaved(false);
              }}
            />
            <span className="muted small">
              <FormattedMessage id="settings.org.days" />
            </span>
          </div>
          <span className="field-help">
            {max ? (
              <FormattedMessage id="settings.org.retentionMax" values={{ n: max }} />
            ) : (
              <FormattedMessage id="settings.org.retentionHelp" />
            )}
          </span>
        </label>
        <button
          type="submit"
          className="btn btn-outline"
          disabled={!editable || busy || Number(days) === tenant.retentionDays}
        >
          {busy ? <Spinner size={15} /> : saved ? <Icon name="check" size={15} /> : null}
          <FormattedMessage id={saved ? "common.saved" : "common.save"} />
        </button>
      </form>
      <ErrorBanner message={error ? errMsg(error) : null} />
    </Section>
  );
}

function Members() {
  const intl = useIntl();
  const errMsg = useErrorMessage();
  const { role, user, tenant } = useSession();
  const manage = canManage(role);
  const [members, setMembers] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ email: "", role: "analyst" });
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(() => {
    endpoints.members().then((d) => setMembers(d.members || []), setError);
  }, []);
  useEffect(load, [load]);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { member } = await endpoints.inviteMember({ email: form.email.trim(), role: form.role });
      setMembers((m) => [...(m || []), member]);
      setForm({ email: "", role: form.role });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(intl.formatMessage({ id: "settings.members.confirmRemove" }, { email: m.email }))) return;
    setRemoving(m.userId);
    setError(null);
    try {
      await endpoints.removeMember(m.userId);
      setMembers((list) => list.filter((x) => x.userId !== m.userId));
    } catch (err) {
      setError(err);
    } finally {
      setRemoving(null);
    }
  };

  const seats = tenant?.entitlements?.seats;

  return (
    <Section
      id="members"
      icon="users"
      titleId="settings.members.title"
      action={
        members && seats ? (
          <span className="muted small">
            <FormattedMessage id="settings.members.seats" values={{ used: members.length, total: seats }} />
          </span>
        ) : null
      }
    >
      <ErrorBanner message={error ? errMsg(error) : null} />
      {!members && !error ? <Loading /> : null}
      {members ? (
        <div className="grid-wrap flush">
          <table className="grid">
            <thead>
              <tr>
                <th>
                  <FormattedMessage id="settings.members.email" />
                </th>
                <th>
                  <FormattedMessage id="settings.members.role" />
                </th>
                <th>
                  <FormattedMessage id="settings.members.since" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.userId}>
                  <td>
                    {m.email}
                    {m.userId === user?.id ? (
                      <span className="muted small">
                        {" "}
                        <FormattedMessage id="settings.members.you" />
                      </span>
                    ) : null}
                  </td>
                  <td>
                    <span className={`role-tag role-${m.role}`}>
                      <FormattedMessage id={`role.${m.role}`} defaultMessage={m.role} />
                    </span>
                  </td>
                  <td className="muted nowrap">
                    <FormattedDate value={m.createdAt} dateStyle="medium" />
                  </td>
                  <td className="end-text">
                    {manage && m.role !== "owner" && m.userId !== user?.id ? (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm danger-text"
                        onClick={() => remove(m)}
                        disabled={removing === m.userId}
                      >
                        {removing === m.userId ? <Spinner size={13} /> : <Icon name="trash" size={14} />}
                        <FormattedMessage id="settings.members.remove" />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {manage ? (
        <form className="form form-inline invite-form" onSubmit={invite}>
          <label className="field grow">
            <span className="field-label">
              <FormattedMessage id="settings.members.inviteEmail" />
            </span>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </label>
          <label className="field">
            <span className="field-label">
              <FormattedMessage id="settings.members.role" />
            </span>
            <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
              {INVITE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {intl.formatMessage({ id: `role.${r}` })}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" className="btn btn-primary" disabled={busy || !form.email}>
            {busy ? <Spinner size={15} /> : <Icon name="plus" size={15} />}
            <FormattedMessage id="settings.members.invite" />
          </button>
        </form>
      ) : null}
    </Section>
  );
}

function Billing() {
  const lp = useLocalePath();
  const errMsg = useErrorMessage();
  const { tenant, role } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const manage = canManage(role);

  const portal = async () => {
    setBusy(true);
    setError(null);
    try {
      const { url } = await endpoints.billingPortal();
      window.location.assign(url);
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  return (
    <Section id="billing" icon="card" titleId="settings.billing.title">
      <dl className="kv kv-cols">
        <div className="kv-row">
          <dt>
            <FormattedMessage id="settings.billing.plan" />
          </dt>
          <dd>
            {tenant?.plan ? <FormattedMessage id={`plan.${tenant.plan}`} defaultMessage={tenant.plan} /> : "—"}
          </dd>
        </div>
        <div className="kv-row">
          <dt>
            <FormattedMessage id="settings.billing.subscription" />
          </dt>
          <dd>{tenant?.subscriptionStatus || "—"}</dd>
        </div>
        {tenant?.entitlements && Object.keys(tenant.entitlements).length ? (
          <>
            <div className="kv-row">
              <dt>
                <FormattedMessage id="settings.billing.concurrency" />
              </dt>
              <dd>{tenant.entitlements.maxConcurrentJobs}</dd>
            </div>
            <div className="kv-row">
              <dt>
                <FormattedMessage id="settings.billing.tierB" />
              </dt>
              <dd>
                <FormattedMessage id={tenant.entitlements.tierB ? "common.yes" : "common.no"} />
              </dd>
            </div>
          </>
        ) : null}
      </dl>
      <div className="btn-row">
        {tenant?.plan ? (
          <button type="button" className="btn btn-outline" onClick={portal} disabled={!manage || busy}>
            {busy ? <Spinner size={15} /> : <Icon name="external" size={15} />}
            <FormattedMessage id="settings.billing.portal" />
          </button>
        ) : (
          <Link className="btn btn-primary" to={lp("/onboarding")}>
            <FormattedMessage id="dashboard.next.subscribe.cta" />
          </Link>
        )}
      </div>
      {!manage ? (
        <p className="muted small">
          <FormattedMessage id="settings.billing.adminOnly" />
        </p>
      ) : null}
      <ErrorBanner message={error ? errMsg(error) : null} />
    </Section>
  );
}

function CreditsLedger() {
  const errMsg = useErrorMessage();
  const lp = useLocalePath();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    setError(null);
    endpoints.credits().then(setData, setError);
  }, []);
  useEffect(load, [load]);

  return (
    <Section
      id="credits"
      icon="coins"
      titleId="settings.credits.title"
      action={
        data ? (
          <span className="credit-total">
            <FormattedMessage id="settings.credits.balance" />
            <strong className="grad-text">
              <Credits value={data.balance} />
            </strong>
          </span>
        ) : null
      }
    >
      <ErrorBanner message={error ? errMsg(error) : null} onRetry={load} />
      {!data && !error ? <Loading /> : null}
      {data && data.ledger?.length === 0 ? (
        <p className="muted small">
          <FormattedMessage id="settings.credits.empty" />
        </p>
      ) : null}
      {data && data.ledger?.length ? (
        <div className="grid-wrap flush ledger-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>
                  <FormattedMessage id="settings.credits.date" />
                </th>
                <th>
                  <FormattedMessage id="settings.credits.kind" />
                </th>
                <th className="end-text">
                  <FormattedMessage id="settings.credits.amount" />
                </th>
                <th>
                  <FormattedMessage id="settings.credits.job" />
                </th>
              </tr>
            </thead>
            <tbody>
              {data.ledger.map((row) => (
                <tr key={row.id}>
                  <td className="muted nowrap">
                    <FormattedDate value={row.createdAt} dateStyle="medium" timeStyle="short" />
                  </td>
                  <td>
                    <span className={`ledger-kind kind-${row.kind}`}>
                      <FormattedMessage id={`ledger.${row.kind}`} defaultMessage={row.kind} />
                    </span>
                  </td>
                  <td className={`end-text mono ${row.amount < 0 ? "bad-text" : "ok-text"}`}>
                    <FormattedNumber value={row.amount} signDisplay="exceptZero" />
                  </td>
                  <td className="mono">
                    {row.jobId ? <Link to={lp(`/jobs/${row.jobId}`)}>{row.jobId.slice(0, 8)}</Link> : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Section>
  );
}

export default function Settings() {
  useDocumentTitle("settings.title");
  const { hash } = useLocation();
  const { tenant } = useSession();

  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash, tenant]);

  return (
    <div className="page">
      <header className="page-head">
        <h1 className="h1">
          <FormattedMessage id="settings.title" />
        </h1>
      </header>
      <nav className="settings-tabs">
        {["organisation", "members", "billing", "credits"].map((s) => (
          <Link key={s} to={{ hash: `#${s}` }} className={hash === `#${s}` ? "is-active" : ""}>
            <FormattedMessage id={`settings.nav.${s}`} />
          </Link>
        ))}
      </nav>
      {tenant?.status && tenant.status !== "active" ? (
        <Banner kind="warn" icon="alert">
          <FormattedMessage id={`tenantStatus.${tenant.status}.notice`} />
        </Banner>
      ) : null}
      <div className="settings-grid">
        <Organisation />
        <Billing />
        <Members />
        <CreditsLedger />
      </div>
    </div>
  );
}
